import translator from '@Components/Utilities/Translator';
import { useState, useRef, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import TranslationText from '@Components/Utilities/TranslationText';
import { toTitleCase } from '@Components/Utilities/SandhillsUtility';
import WizardBanner from '@Components/Wizard/WizardBanner';
import { gtmPushEvent, sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';


const FeedbackModal = loadable(() => import('@Components/Shared/Modals/FeedbackModal'), true);
const WizardResults = loadable(() => import('@Components/Wizard/WizardResults'), true);

const WizardView = (props) => {

    const routes = useRecoilValue(atoms.routes);

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    const followupRef = useRef(null);

    useEffect(() => {
        if (showTowVehicleFollowup && followupRef.current) {
            followupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [showTowVehicleFollowup]);

    const getAnswer = (questionKey, answers = selectedAnswers) =>
        answers.find(a => a.QuestionKey === questionKey);

    const upsertAnswer = (questionKey, answerKey, answerText, answerCaption, answers = selectedAnswers) => ([
        ...answers.filter(a => a.QuestionKey !== questionKey),
        { QuestionKey: questionKey, AnswerKey: answerKey, AnswerText: answerText, AnswerCaption: answerCaption }
    ]);

    const goNextOrResults = (answers) => {
        if (currentQuestion < props.Questions.length - 1) {
            setCurrentQuestion((prevIndex) => prevIndex + 1);
        } else {
            handleSeeResults(answers);
        }
    };

    const nextQuestion = (answerKey, answerText) => {
        const question = props.Questions[currentQuestion];
        const questionKey = question.QuestionKey;

        const selectedAnswer = question.Answers.find(a => a.AnswerKey === answerKey);
        const answerCaption = selectedAnswer?.AnswerCaption;

        let updatedAnswers = upsertAnswer(questionKey, answerKey, answerText, answerCaption);

        if (questionKey === 'tow-rv' && answerKey !== 'yes-have-vehicle') {
            updatedAnswers = updatedAnswers.filter(a => a.QuestionKey !== 'tow-vehicle-type');
        }

        setSelectedAnswers(updatedAnswers);

        if (questionKey === 'tow-rv' && answerKey === 'yes-have-vehicle') {
            return;
        }

        goNextOrResults(updatedAnswers);
    };

    const currentQuestionModel = props.Questions[currentQuestion];
    const isTowQuestion = currentQuestionModel?.QuestionKey === 'tow-rv';
    const towAnswer = getAnswer('tow-rv');
    const showTowVehicleFollowup = isTowQuestion && towAnswer?.AnswerKey === 'yes-have-vehicle';

    const onTowVehicleTypeSelect = (answerKey, answerText) => {
        const updatedAnswers = upsertAnswer('tow-vehicle-type', answerKey, answerText);
        setSelectedAnswers(updatedAnswers);
        goNextOrResults(updatedAnswers);
    };

    const prevQuestion = () => {
        setCurrentQuestion((prevIndex) => prevIndex - 1);
    };

    const handleSeeResults = (answers = selectedAnswers) => {
        gtmPushEvent(gtmEventType.wizardSubmission);
        setLoading(true);

        const formData = new FormData();
        answers.forEach((answer, index) => {
            formData.append(`selectedAnswers[${index}].QuestionKey`, answer.QuestionKey);
            formData.append(`selectedAnswers[${index}].AnswerKey`, answer.AnswerKey);
        });

        sandhillsRequest({
            url: routes.WizardResults,
            method: 'post',
            data: formData,
            
        }).then((data) => {
            if (data?.success) {
                setResults(data.data);
                setShowResults(true);
                setLoading(false);
            } else {
                console.error('Failed to get recommendations');
            }
        });
    };

    if (loading) {
        return (
            <div className="wizard-loading-overlay">
                <img src="/Content/RVUniverse/logo.svg"></img>
                <strong>{translator.translate("sandhills.tradesites.mvc.findyourbestrvmatch")}</strong>
                <div className="loading">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        )
    }

    if (showResults && results) {
        return <WizardResults props={props} results={results} selectedAnswers={selectedAnswers} />;
    }

    return (
        <div className='wizard-view'>
            <h1>{translator.translate("sandhills.tradesites.mvc.findtherightrvforyourlifestyle")}</h1>
            <ol className='wizard-progress'>
                {Array.from({ length: currentQuestion + 1 }).map((_, index) => (
                    <li key={index} className='active'></li>
                ))}
                {Array.from({ length: props.Questions.length - currentQuestion - 1 }).map((_, index) => (
                    <li key={currentQuestion + 1 + index}></li>
                ))}
            </ol>
            {props.Questions && props.Questions.length > 0 ? (
                <div className='wizard-question'>
                    <h2>{props.Questions[currentQuestion].QuestionText}</h2>
                    <div className='answers-container'>
                        {props.Questions[currentQuestion].Answers.map((answer, index) => (
                            <button
                                key={index}
                                className='wizard-answer'
                                onClick={() => nextQuestion(answer.AnswerKey, answer.AnswerText)}
                            >
                                <img src={answer.ImageUrl} role="presentation" />
                                <strong>{answer.AnswerText}</strong>
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}
            {showTowVehicleFollowup && (
                <div ref={followupRef} className='wizard-question sub-question'>
                    <h3>{translator.translate("sandhills.tradesites.mvc.areyoudrivingapickuptruck")}</h3>
                    <div className='answers-container'>
                        <button
                            className='wizard-answer sub-question'
                            onClick={() => onTowVehicleTypeSelect('pickup-yes', translator.translate("sandhills.tradesites.mvc.yespickuptruck"))}
                        >
                            <strong>{translator.translate("sandhills.tradesites.mvc.yespickuptruck")}</strong>
                        </button>
                        <button
                            className='wizard-answer sub-question'
                            onClick={() => onTowVehicleTypeSelect('pickup-no', translator.translate("sandhills.tradesites.mvc.nosuvcrossovervan"))}
                        >
                            <strong>{translator.translate("sandhills.tradesites.mvc.nosuvcrossovervan")}</strong>
                        </button>
                    </div>
                </div>
            )}
            {currentQuestion == props.Questions.length - 1 && !(selectedAnswers.filter(answer => answer.AnswerKey !== null).length > 0) &&
                <p className="error">{translator.translate("sandhills.tradesites.mvc.pleaseselectananswertofindrv")}</p>
            }
            <div className='wizard-button-container'>
                {currentQuestion > 0 &&
                    <>
                        <button className='wizard-button' onClick={prevQuestion}>
                            <i className='fa-solid fa-arrow-left-long'></i>
                            {translator.translate("sandhills.back")}
                        </button>
                    </>
                }
                {currentQuestion < props.Questions.length - 1 &&
                    <>
                        <button className='wizard-button' onClick={() => nextQuestion(null)}>
                            {translator.translate("sandhills.tradepubwebsite.mvc.next")}
                            <i className='fa-solid fa-arrow-right-long'></i>
                        </button>
                    </>
                }
                {currentQuestion == props.Questions.length - 1 && selectedAnswers.filter(answer => answer.AnswerKey !== null).length > 0 &&
                    <button
                        className='wizard-button'
                        onClick={() => handleSeeResults(selectedAnswers)}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} /> : translator.translate("sandhills.tradesites.mvc.seeresults")}
                    </button>
                }
            </div>
        </div>
    );
}
export default WizardView;
global.WizardView = WizardView;